'use client';

import { cn } from '@sunfish/lib/utils';
import { useCallback, useEffect, useRef, useState } from 'react';

interface ExpandableTextProps {
	children: React.ReactNode;
	lines?: number;
	className?: string;
	showMoreClassName?: string;
}

export function ExpandableText({ children, lines = 3, className, showMoreClassName }: ExpandableTextProps) {
	const [isExpanded, setIsExpanded] = useState(false);
	const [needsTruncation, setNeedsTruncation] = useState(false);
	const contentRef = useRef<HTMLDivElement>(null);

	const checkTruncation = useCallback(() => {
		const element = contentRef.current;
		if (!element) return;

		const lineHeight = parseFloat(getComputedStyle(element).lineHeight) || 20;
		const maxHeight = lineHeight * lines;
		setNeedsTruncation(element.scrollHeight > maxHeight + 2);
	}, [lines]);

	useEffect(() => {
		checkTruncation();

		const resizeObserver = new ResizeObserver(checkTruncation);
		if (contentRef.current) {
			resizeObserver.observe(contentRef.current);
		}

		return () => resizeObserver.disconnect();
	}, [checkTruncation, children]);

	return (
		<div className={className}>
			<div
				ref={contentRef}
				className={cn({
					'line-clamp-3': !isExpanded && needsTruncation && lines === 3,
					'line-clamp-2': !isExpanded && needsTruncation && lines === 2,
					'line-clamp-4': !isExpanded && needsTruncation && lines === 4,
					'line-clamp-5': !isExpanded && needsTruncation && lines === 5
				})}
				style={
					!isExpanded && needsTruncation && ![2, 3, 4, 5].includes(lines)
						? {
								display: '-webkit-box',
								WebkitLineClamp: lines,
								WebkitBoxOrient: 'vertical',
								overflow: 'hidden'
							}
						: undefined
				}
			>
				{children}
			</div>
			{needsTruncation && (
				<button
					type="button"
					onClick={() => setIsExpanded(!isExpanded)}
					className={cn('text-brand hover:text-brand/80 text-sm font-medium mt-1 cursor-pointer', showMoreClassName)}
				>
					{isExpanded ? 'Show less' : 'Show more'}
				</button>
			)}
		</div>
	);
}
